import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import {
  Campo,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  Secao,
  Vazio,
} from '../components/ui';
import { limpar } from './Eventos';

interface Conta {
  id: string;
  label: string;
  wabaId: string;
  phoneNumberId: string;
  /** Vem da Meta no "Testar API". Nulo até a primeira verificação. */
  displayPhone: string | null;
  verifiedName: string | null;
  /** App Meta do cliente. Nulo nas contas anteriores ao modelo de app próprio. */
  metaAppId: string | null;
  /** Responde o desafio da Meta na URL desta conta. Não assina nada. */
  webhookVerifyToken: string;
  onboardingStatus: string;
  qualityRating: string | null;
  messagingTier: string | null;
  active: boolean;
  tokenExpiresAt: string | null;
  lastSyncAt: string | null;
  lastErrorAt: string | null;
  lastErrorMsg: string | null;
  podeEnviar: boolean;
  pendencia: string | null;
}

const CORES_STATUS: Record<string, string> = {
  NAO_CONECTADA: 'muted',
  AGUARDANDO_AUTORIZACAO: 'info',
  CONECTADA: 'info',
  TEMPLATES_PENDENTES: 'warning',
  PRONTA: 'success',
  TOKEN_EXPIRADO: 'danger',
  DESCONECTADA: 'danger',
};

const ROTULOS_STATUS: Record<string, string> = {
  NAO_CONECTADA: 'Não conectada',
  AGUARDANDO_AUTORIZACAO: 'Aguardando autorização',
  CONECTADA: 'Conectada',
  TEMPLATES_PENDENTES: 'Templates pendentes',
  PRONTA: 'Pronta',
  TOKEN_EXPIRADO: 'Token expirado',
  DESCONECTADA: 'Desconectada',
};

interface InfoWebhook {
  /** Prefixo; o id da conta completa a URL. */
  caminhoBase: string;
  provider: 'fake' | 'meta';
}

interface Diagnostico {
  ok: boolean;
  detalhe: string;
  simulado?: boolean;
  status?: string;
  callbackUrl?: string;
  tipo: 'api' | 'webhook';
}

export function ConfigWhatsapp() {
  const cliente = useQueryClient();
  const { liberado } = useAuth();
  // Mesma chave que tranca a rota no servidor (`@RequerRecurso('jornadas')`).
  // Ler a mesma fonte é o que evita a tela e a API discordarem.
  const envioLiberado = liberado('jornadas');
  const [conectando, setConectando] = useState(false);
  const [testando, setTestando] = useState<Conta | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  const consulta = useQuery({ queryKey: ['whatsapp'], queryFn: () => api<Conta[]>('/whatsapp/accounts') });

  const webhook = useQuery({
    queryKey: ['whatsapp-webhook'],
    queryFn: () => api<InfoWebhook>('/whatsapp/webhook'),
  });

  // A origem vem do navegador, não do servidor: é o mesmo endereço por onde
  // esta tela chegou, então não há como divergir do que a Meta vai chamar.
  /** `active` é o que conta: desconectada não ocupa a vaga. */
  const contaAtiva = (consulta.data ?? []).find((c) => c.active) ?? null;
  const temConta = !!contaAtiva;

  // Montada com a origem do navegador, e não com uma variável do servidor: é o
  // mesmo endereço por onde esta tela chegou, então não há como divergir do que
  // a Meta vai chamar. O servidor monta a mesma coisa a partir de APP_URL, e as
  // duas precisam bater — se o APP_URL estiver errado, o "Testar webhook"
  // acusa, porque compara a URL registrada com a que ele espera.
  const urlDaConta =
    contaAtiva && webhook.data
      ? `${window.location.origin}${webhook.data.caminhoBase}/${contaAtiva.id}`
      : '';

  async function copiar(texto: string, qual: string) {
    await navigator.clipboard.writeText(texto);
    setCopiado(qual);
    setTimeout(() => setCopiado(null), 2000);
  }

  const conectar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api('/whatsapp/accounts', { method: 'POST', body: limpar(dados) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['whatsapp'] });
      setConectando(false);
    },
  });

  const desconectar = useMutation({
    mutationFn: (id: string) => api(`/whatsapp/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['whatsapp'] }),
  });

  // Um resultado por conta e por tipo, para os dois botões não brigarem pelo
  // mesmo espaço na tela quando alguém clica nos dois.
  const [diagnostico, setDiagnostico] = useState<Record<string, Diagnostico>>({});
  const [mostrarTranca, setMostrarTranca] = useState(false);
  const [mostrarDicaWebhook, setMostrarDicaWebhook] = useState(false);
  const [registro, setRegistro] = useState<{ ok: boolean; detalhe: string } | null>(null);

  const registrar = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; detalhe: string }>(`/whatsapp/accounts/${id}/registrar-webhook`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      setRegistro(res);
      // Registrar muda o que o diagnóstico veria; o anterior fala do estado
      // de antes.
      setDiagnostico({});
    },
    onError: (erro: Error) => setRegistro({ ok: false, detalhe: erro.message }),
  });
  const [editando, setEditando] = useState<Conta | null>(null);

  const salvar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, string> }) =>
      api(`/whatsapp/accounts/${id}`, {
        method: 'PATCH',
        body: {
          ...limpar(dados),
          // `limpar` tira os vazios, e para o token isso é o certo: vazio
          // significa "mantenha o atual", porque o servidor nunca o devolve
          // para preencher o campo.
          //
          // Para a validade, não. Vazio aqui quer dizer "não expira", e é o
          // caso de quem acabou de trocar um token temporário por um de system
          // user: mantida, a validade velha marcaria a conta como expirada com
          // uma credencial perfeitamente boa. `null` limpa de verdade.
          tokenExpiresAt: dados.tokenExpiresAt || null,
        },
      }),
    onSuccess: (_r, { id }) => {
      cliente.invalidateQueries({ queryKey: ['whatsapp'] });
      setEditando(null);
      // O diagnóstico antigo falava de uma credencial que pode ter acabado de
      // ser trocada: mantê-lo na tela seria mostrar um erro já resolvido.
      setDiagnostico((d) => {
        const { [id]: _fora, ...resto } = d;
        return resto;
      });
    },
  });

  const verificar = useMutation({
    mutationFn: ({ id, tipo }: { id: string; tipo: 'api' | 'webhook' }) =>
      api<Diagnostico>(`/whatsapp/accounts/${id}/verificar-${tipo}`, { method: 'POST' }).then(
        (r) => ({ ...r, tipo }),
      ),
    onSuccess: (res, { id }) => {
      setDiagnostico((d) => ({ ...d, [id]: res }));
      // A verificação da API grava qualidade e faixa de envio: sem recarregar,
      // a tela continuaria mostrando os "—" que o diagnóstico acabou de
      // preencher.
      cliente.invalidateQueries({ queryKey: ['whatsapp'] });
    },
    onError: (erro: Error, { id, tipo }) =>
      setDiagnostico((d) => ({ ...d, [id]: { ok: false, detalhe: erro.message, tipo } })),
  });

  return (
    <Secao
        titulo="WhatsApp"
        descricao="Cada organização usa uma conta WhatsApp Business (WABA), no app Meta dela."
        acao={
          // Uma conta ativa por cliente. Oferecer o botão e recusar no envio
          // seria pedir para a pessoa preencher sete campos para depois ouvir
          // não — o servidor recusa igual (409), isto só evita a viagem.
          temConta ? undefined : (
            <button className="btn btn-primary" onClick={() => setConectando(true)}>
              <i className="ti ti-plug" /> Conectar conta
            </button>
          )
        }
      >

      {/* Antes da lista, de propósito: com o provider falso, tudo abaixo daqui
          responde "deu certo" sem falar com a Meta — inclusive o Testar envio.
          Descobrir isso depois do clique custa uma hora de procurar a mensagem
          num celular que nunca ia recebê-la. */}
      {webhook.data?.provider === 'fake' && (
        <div
          className="aviso"
          style={{ marginBottom: 16, borderColor: 'var(--warning)' }}
        >
          <i className="ti ti-alert-triangle" style={{ color: 'var(--warning)' }} />
          <span>
            Esta instalação está em <strong>modo simulado</strong> — nada é enviado à Meta.
            "Testar envio" vai responder <em>enfileirado</em> sem que mensagem nenhuma saia.
            Para valer, o servidor precisa de <code>WHATSAPP_PROVIDER=meta</code>.
          </span>
        </div>
      )}

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.length === 0 ? (
          <Vazio
            icone="ti-brand-whatsapp"
            titulo="Nenhuma conta conectada"
            descricao="Sem uma WABA conectada não há como conversar com participantes — o WhatsApp é o único canal do produto."
            acao={
              <button className="btn btn-primary" onClick={() => setConectando(true)}>
                <i className="ti ti-plug" /> Conectar conta
              </button>
            }
          />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {consulta.data.map((conta) => (
              <div key={conta.id} className="card">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
                    >
                      <i className="ti ti-brand-whatsapp" style={{ color: 'var(--whatsapp)' }} />
                      <strong style={{ fontSize: 14 }}>{conta.label}</strong>
                      <Tag type={CORES_STATUS[conta.onboardingStatus] ?? 'muted'}>
                        {ROTULOS_STATUS[conta.onboardingStatus] ?? conta.onboardingStatus}
                      </Tag>
                    </div>
                    {/* Vazio até a Meta responder: o número e o nome chegam no
                        "Testar API", não do teclado. Enquanto não chegam, dizer
                        o que falta vale mais do que uma linha em branco. */}
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {conta.displayPhone ? (
                        <>
                          {conta.displayPhone}
                          {conta.verifiedName && ` · ${conta.verifiedName}`}
                        </>
                      ) : (
                        <em>Número confirmado pela Meta no primeiro “Testar API”.</em>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Cadeado discreto no lugar da faixa: o envio desligado é
                        um estado permanente desta fase, e um aviso do tamanho
                        de um erro, repetido em toda visita, ensina a ignorar
                        avisos. Quem quiser o porquê, clica. */}
                    {!envioLiberado && (
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Envio desligado nesta instalação — clique para entender"
                        aria-expanded={mostrarTranca}
                        onClick={() => setMostrarTranca((v) => !v)}
                        style={{ color: 'var(--warning)' }}
                      >
                        <i className="ti ti-lock" /> Envio desligado
                      </button>
                    )}

                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditando(conta)}
                    >
                      <i className="ti ti-pencil" /> Configurar
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={verificar.isPending}
                      onClick={() => verificar.mutate({ id: conta.id, tipo: 'api' })}
                    >
                      <i className="ti ti-plug-connected" /> Testar API
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={verificar.isPending}
                      onClick={() => verificar.mutate({ id: conta.id, tipo: 'webhook' })}
                    >
                      <i className="ti ti-webhook" /> Testar webhook
                    </button>

                    {/* Some por inteiro quando o disparo está trancado, em vez
                        de ficar cinza: um botão desabilitado convida a procurar
                        o que falta na conta, e aqui não falta nada nela — a
                        instalação é que ainda não libera envio. */}
                    {envioLiberado && (
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={!conta.podeEnviar}
                        onClick={() => setTestando(conta)}
                      >
                        <i className="ti ti-send" /> Testar envio
                      </button>
                    )}
                    {conta.active && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => desconectar.mutate(conta.id)}
                      >
                        Desconectar
                      </button>
                    )}
                  </div>
                </div>

                {diagnostico[conta.id] && (
                  <div
                    className="aviso"
                    style={{
                      marginTop: 14,
                      borderColor: diagnostico[conta.id]!.ok
                        ? 'var(--success)'
                        : 'var(--warning)',
                    }}
                  >
                    <i
                      className={`ti ${diagnostico[conta.id]!.ok ? 'ti-circle-check' : 'ti-alert-triangle'}`}
                      style={{
                        color: diagnostico[conta.id]!.ok ? 'var(--success)' : 'var(--warning)',
                      }}
                    />
                    <span>
                      <strong>
                        {diagnostico[conta.id]!.tipo === 'api' ? 'API' : 'Webhook'}:{' '}
                        {diagnostico[conta.id]!.ok ? 'tudo certo.' : 'atenção.'}
                      </strong>{' '}
                      {diagnostico[conta.id]!.detalhe}
                      {diagnostico[conta.id]!.status && (
                        <> Status do número: <code>{diagnostico[conta.id]!.status}</code>.</>
                      )}
                    </span>
                  </div>
                )}

                {/* Com o envio trancado, a pendência da conta é ruído: ela
                    explica o que falta para disparar, e disparar não é o
                    assunto agora. Uma tela que cobra duas coisas ao mesmo tempo
                    faz a pessoa resolver a errada. */}
                {envioLiberado
                  ? conta.pendencia && (
                      <div className="aviso" style={{ marginTop: 14 }}>
                        <i className="ti ti-info-circle" />
                        <span>{conta.pendencia}</span>
                      </div>
                    )
                  : mostrarTranca && (
                      <div className="aviso" style={{ marginTop: 14 }}>
                        <i className="ti ti-lock" />
                        <span>
                          <strong>Envio desligado nesta instalação.</strong> A conta pode ser
                          cadastrada e conferida, e as mensagens que chegarem são recebidas
                          normalmente — mas nada parte daqui para participante nenhum.
                        </span>
                      </div>
                    )}

                {conta.lastErrorMsg && (
                  <div className="form-erro" style={{ marginTop: 12, marginBottom: 0 }}>
                    Último erro: {conta.lastErrorMsg}
                  </div>
                )}

                <div className="divider" style={{ margin: '16px 0' }} />

                <dl className="lista-dados">
                  <dt>WABA ID</dt>
                  <dd className="mono">{conta.wabaId}</dd>
                  <dt>Phone Number ID</dt>
                  <dd className="mono">{conta.phoneNumberId}</dd>
                  <dt>Qualidade</dt>
                  <dd>{conta.qualityRating ?? '—'}</dd>
                  <dt>Faixa de envio</dt>
                  <dd>{conta.messagingTier ?? '—'}</dd>
                  <dt>Token</dt>
                  <dd>
                    {conta.tokenExpiresAt
                      ? `expira em ${new Date(conta.tokenExpiresAt).toLocaleDateString('pt-BR')}`
                      : 'permanente (system user)'}
                  </dd>
                </dl>
              </div>
            ))}
          </div>
        ))}

      {/* Cadastrar a conta é metade do caminho. Sem registrar esta URL no painel
          da Meta, nada do que o participante escrever chega aqui — e essa metade
          não aparecia em lugar nenhum do produto. */}
      {contaAtiva && (
        <div className="card" style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
              flexWrap: 'wrap',
            }}
          >
            <i className="ti ti-webhook" style={{ color: 'var(--accent)' }} />
            <strong style={{ fontSize: 14 }}>Webhook desta conta</strong>

            {/* Mesmo tratamento do cadeado de envio: é uma instrução que vale
                uma vez, na configuração, e não um alerta. Repetida em toda
                visita, treina o olho a pular a faixa amarela — e aí a próxima,
                que importa, some junto. */}
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginLeft: 'auto' }}
              aria-expanded={mostrarDicaWebhook}
              onClick={() => setMostrarDicaWebhook((v) => !v)}
            >
              <i className={`ti ${mostrarDicaWebhook ? 'ti-chevron-up' : 'ti-help-circle'}`} />
              {mostrarDicaWebhook ? 'Ocultar' : 'Como configurar'}
            </button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            Cada cliente tem a própria URL, no app Meta dele. Com o App ID e o App Secret
            preenchidos, o botão abaixo registra tudo sem você abrir o painel.
          </p>

          <dl className="lista-dados">
            <dt>URL de callback</dt>
            <dd style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="mono">{urlDaConta}</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => void copiar(urlDaConta, 'url')}
              >
                <i className={`ti ${copiado === 'url' ? 'ti-check' : 'ti-copy'}`} />
                {copiado === 'url' ? 'Copiado' : 'Copiar'}
              </button>
            </dd>

            <dt>Verificar token</dt>
            <dd style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="mono">{contaAtiva.webhookVerifyToken}</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => void copiar(contaAtiva.webhookVerifyToken, 'token')}
              >
                <i className={`ti ${copiado === 'token' ? 'ti-check' : 'ti-copy'}`} />
                {copiado === 'token' ? 'Copiado' : 'Copiar'}
              </button>
            </dd>
          </dl>

          <div style={{ marginTop: 16 }}>
            <button
              className="btn btn-primary"
              disabled={!contaAtiva.metaAppId || registrar.isPending}
              title={
                contaAtiva.metaAppId
                  ? undefined
                  : 'Preencha App ID e App Secret em Configurar para registrar automaticamente.'
              }
              onClick={() => registrar.mutate(contaAtiva.id)}
            >
              <i className="ti ti-cloud-upload" />
              {registrar.isPending ? 'Registrando…' : 'Registrar na Meta'}
            </button>
          </div>

          {registro && (
            <div
              className="aviso"
              style={{
                marginTop: 14,
                borderColor: registro.ok ? 'var(--success)' : 'var(--warning)',
              }}
            >
              <i
                className={`ti ${registro.ok ? 'ti-circle-check' : 'ti-alert-triangle'}`}
                style={{ color: registro.ok ? 'var(--success)' : 'var(--warning)' }}
              />
              <span>{registro.detalhe}</span>
            </div>
          )}

          {mostrarDicaWebhook && (
            <div className="aviso" style={{ marginTop: 14 }}>
              <i className="ti ti-info-circle" />
              <span>
                Para fazer à mão: em <em>developers.facebook.com</em> → app do cliente →
                WhatsApp → Configuração → Webhook → Editar, cole os dois valores acima e{' '}
                <strong>assine o campo <code>messages</code></strong>. Sem isso a URL fica
                registrada e <strong>não chega nada</strong> — é o esquecimento mais comum
                aqui, e o <strong>Testar webhook</strong> confere exatamente isso.
              </span>
            </div>
          )}
        </div>
      )}

      {editando && (
        <Modal
          titulo="Configurar conta do WhatsApp"
          descricao={editando.displayPhone ?? undefined}
          aoFechar={() => setEditando(null)}
          largura={600}
        >
          <Form aoEnviar={(dados) => salvar.mutate({ id: editando.id, dados })}>
            {salvar.isError && <div className="form-erro">{(salvar.error as Error).message}</div>}

            {/* Identificadores fora do formulário, de propósito: a Meta os
                emite, o webhook roteia por eles e a unicidade do
                phoneNumberId é global. Editá-los não seria corrigir um dado —
                seria apontar para outra conta. Para isso existe desconectar.
                O telefone e o nome verificado saem daqui pelo motivo vizinho:
                a Meta os define e o "Testar API" os grava. */}
            <div className="aviso" style={{ marginBottom: 16 }}>
              <i className="ti ti-info-circle" />
              <span>
                WABA <code>{editando.wabaId}</code> · Phone Number ID{' '}
                <code>{editando.phoneNumberId}</code>. Esses dois não mudam — para trocar de
                número, desconecte e conecte de novo. O telefone exibido e o nome verificado
                vêm da Meta no <strong>Testar API</strong>.
              </span>
            </div>

            <Campo
              label="Identificação interna"
              name="label"
              obrigatorio
              valorInicial={editando.label}
            />

            <LinhaCampos>
              <Campo
                label="App ID do Meta"
                name="metaAppId"
                valorInicial={editando.metaAppId ?? ''}
                ajuda="Do app Meta deste cliente. Em Configurações → Básico."
              />
              <Campo
                label="App Secret do Meta"
                name="appSecret"
                tipo="password"
                ajuda="Vazio mantém o atual. É ele que valida a assinatura do que a Meta entrega."
              />
            </LinhaCampos>

            <Campo
              label="Access token"
              name="accessToken"
              tipo="password"
              ajuda="Deixe vazio para manter o atual. O servidor guarda cifrado e nunca devolve — por isso não há como mostrá-lo aqui."
            />

            <Campo
              label="Validade do token"
              name="tokenExpiresAt"
              tipo="date"
              valorInicial={editando.tokenExpiresAt?.slice(0, 10) ?? ''}
              ajuda="Vazio para token permanente de system user."
            />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setEditando(null)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? 'Salvando…' : 'Salvar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {conectando && (
        <Modal
          titulo="Conectar conta do WhatsApp"
          descricao="Credenciais do app Meta da organização. Encontradas em developers.facebook.com → seu app → WhatsApp → Configuração da API."
          aoFechar={() => setConectando(false)}
          largura={600}
        >
          <Form aoEnviar={(dados) => conectar.mutate(dados)}>
            {conectar.isError && <div className="form-erro">{(conectar.error as Error).message}</div>}

            <Campo
              label="Identificação interna"
              name="label"
              obrigatorio
              autoFocus
              placeholder="Número principal"
              ajuda="Só para você reconhecer a conta nesta tela."
            />

            <LinhaCampos>
              <Campo label="WABA ID" name="wabaId" obrigatorio />
              <Campo label="Phone Number ID" name="phoneNumberId" obrigatorio />
            </LinhaCampos>

            {/* Os dois andam juntos, e o servidor recusa um sem o outro: o ID
                registra o webhook, o secret confere a assinatura do que chega. */}
            <LinhaCampos>
              <Campo
                label="App ID do Meta"
                name="metaAppId"
                obrigatorio
                ajuda="Do app Meta deste cliente. Em Configurações → Básico."
              />
              <Campo
                label="App Secret do Meta"
                name="appSecret"
                tipo="password"
                obrigatorio
                ajuda="Mesma tela. É ele que valida a assinatura dos webhooks."
              />
            </LinhaCampos>

            <Campo
              label="Access token"
              name="accessToken"
              tipo="password"
              obrigatorio
              ajuda="Guardado cifrado (AES-256-GCM) e nunca devolvido pela API. Para trocar, envie um novo."
            />

            <Campo
              label="Validade do token"
              name="tokenExpiresAt"
              tipo="date"
              ajuda="Deixe vazio para token permanente de system user."
            />

            <div className="aviso">
              <i className="ti ti-info-circle" />
              <span>
                A conta entra como <strong>Conectada</strong>. Para iniciar conversas com quem
                ainda não escreveu, é preciso um template aprovado pela Meta.
              </span>
            </div>

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setConectando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={conectar.isPending}>
                {conectar.isPending ? 'Conectando…' : 'Conectar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {testando && <ModalTeste conta={testando} aoFechar={() => setTestando(null)} />}
    </Secao>
  );
}

function ModalTeste({ conta, aoFechar }: { conta: Conta; aoFechar: () => void }) {
  const enviar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api<{ jobId: string; mensagem: string }>(`/whatsapp/accounts/${conta.id}/test`, {
        method: 'POST',
        body: { para: dados.para, texto: dados.texto },
      }),
  });

  return (
    <Modal titulo="Testar envio" descricao={`Pela conta ${conta.label}.`} aoFechar={aoFechar}>
      {enviar.isSuccess ? (
        <>
          <div className="aviso">
            <i className="ti ti-check" />
            <span>{enviar.data.mensagem}</span>
          </div>
          <ModalRodape>
            <button className="btn btn-primary" onClick={aoFechar}>
              Fechar
            </button>
          </ModalRodape>
        </>
      ) : (
        <Form aoEnviar={(dados) => enviar.mutate(dados)}>
          {enviar.isError && <div className="form-erro">{(enviar.error as Error).message}</div>}

          <Campo
            label="Número de destino"
            name="para"
            obrigatorio
            autoFocus
            placeholder="5511987654321"
            ajuda="Formato internacional, só dígitos."
          />
          <Campo
            label="Mensagem"
            name="texto"
            valorInicial="Teste de conexão MatériaPrimAi Eventos."
          />

          <div className="aviso">
            <i className="ti ti-clock" />
            <span>
              A Meta só entrega texto livre para quem falou com este número nas últimas 24
              horas. Fora dessa janela, é preciso template aprovado.
            </span>
          </div>

          <ModalRodape>
            <button type="button" className="btn btn-secondary" onClick={aoFechar}>
              Cancelar
            </button>
            <button className="btn btn-primary" type="submit" disabled={enviar.isPending}>
              {enviar.isPending ? 'Enfileirando…' : 'Enviar teste'}
            </button>
          </ModalRodape>
        </Form>
      )}
    </Modal>
  );
}
